import {
	Dropdown,
	camelCaseToSpace,
	designationMappingWithTechStack,
	generatePositionTitle,
	generateTechStackName,
	locationPreferenceOptions,
	skillNameToIdMapping,
	techStackMappingWithSkills,
	validateEmailAddress,
	validateName,
	validatePhoneNumber,
	isDecimal,
} from 'shared-utils';
import { UseMutationResult } from 'react-query';
import React, { useEffect, useState } from 'react';
import { Button, Label, Radio, Spinner, TextInput, Textarea, ToggleSwitch } from 'flowbite-react';
import Select, { MultiValue, SingleValue } from 'react-select';
import {
	GetPositionsForRecruiterAPIResponse,
	InterviewFormatType,
	MyCompanyType,
	MyPositionType,
	PositionEducationRequirementType,
	PositionTitle,
	PositionType,
	RegisterPosition,
	RegisterPositionAPIResponse,
	SingleRoundLabel,
	TechStacksForPosition,
	UIUser,
	WorkingMode,
} from 'types';
import { IoMdAdd, IoMdRemove } from 'react-icons/io';
import { useUserContext } from 'src/context/UserProvider';
import { useRecommendationsContext } from 'src/context/RecommendationsProvider';
import moment from 'moment';
import _ from 'lodash';
import { useGetAllRecruitersOfCompany, useGetCompanyByIdQuery } from 'src/hooks/company.hooks';
import { useGetLMSTechStacksQuery, useGetDropdownItemsByCategory } from 'src/hooks/dropdownMenu.hooks';
import { LMSTechStack } from 'src/apis/dropdownMenu';
import type {
	PositionTitle as PositionTitleConfig,
	PositionTechStack as PositionTechStackConfig,
	Skill as PositionSkill,
} from 'src/apis/positionConfiguration';
import {
	useGetPositionTitlesQuery,
	useGetTechStacksForPositionTitleQuery,
	useGetSkillsForTechStackQuery,
} from '../../hooks/positionConfiguration.hooks';
import HRSelection from './HRSelection';
import useDisclosure from 'src/hooks/utils/useDisclosure';
import ConfirmCompanyNameModal from './ConfirmCompanyNameModal';
import { PositionEducationRequirementTypeOptions } from 'src/utils/recruiter.util';
import { Drawer } from '../shared/Drawer';
import { useMediaQuery } from '@mui/material';
import CompanyAccountManagers from './CompanyAccountManagers';
import SelectRoundType from './SelectRoundType';

interface Props {
	companyId?: string;
	registerFn: UseMutationResult<RegisterPositionAPIResponse, unknown, RegisterPosition, unknown>;
	formStep: number;
	setFormStep: (step: number) => void;
	accountManagers: GetPositionsForRecruiterAPIResponse['accountManagers'];
}

function CreatePosition({ registerFn, companyId, formStep, setFormStep, accountManagers }: Props) {
	const { isAccountManagerLoggedIn, isRecruiterLoggedIn, user } = useUserContext();
	const { selectedCompany, setSelectedCompany } = useRecommendationsContext();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const isMobile = useMediaQuery('(max-width:960px)');
	const [openAccountManagerDrawer, setOpenAccountManagerDrawer] = useState(false);
	const [hasTouchedContinue, setHasTouchedContinue] = useState(false);
	const [showPrePlacementCallRecordingToStudent, setShowPrePlacementCallRecordingToStudent] = useState<boolean>(false);
	const [prepMaterialLinks, setPrepMaterialLinks] = useState<string[]>(['']);
	const [showPrepMaterialToStudent, setShowPrepMaterialToStudent] = useState<boolean>(false);

	const [positionTypes, setPositionTypes] = useState<Dropdown[]>([]);
	const [positionTitles, setPositionTitles] = useState<Dropdown[]>([]);
	const [techStacks, setTechStacks] = useState<Dropdown[]>([]);
	const [skillsList, setSkillsList] = useState<Dropdown[]>([]);
	const [selectedCompanyId, setSelectedCompanyId] = useState<string>(companyId || '');
	const [disableNewRecruiterFields, setDisableNewRecruiterFields] = useState<boolean>(false);
	const [experienceInMonths, setExperienceInMonths] = useState({ min: 0, max: 0 });
	const [experienceInYears, setExperienceInYears] = useState({ min: 0, max: 0 });
	const [selectedExpInput, setSelectedExpInput] = useState<SingleValue<Dropdown>>({ value: 'years', label: 'Years' });
	const [expError, setExpError] = useState(false);
	const [existingRec, setExistingRec] = useState<UIUser | undefined>();
	const [autoRecommendationEnable, setAutoRecommendationEnable] = useState<boolean>(true);
	const [lmsTechStacks, setLmsTechStacks] = useState<Array<{ techStack: string; priority: number }>>([]);
	const [subRoles, setSubRoles] = useState<string[]>([]);
	const { data: lmsTechStacksData, isLoading: isLoadingLMSTechStacks, error: lmsTechStacksError } = useGetLMSTechStacksQuery(undefined, true); // Get only active LMS TechStacks
	// Fetch SubRole items from database (only items where showInMenu is true)
	const { data: subRoleDataForCreate } = useGetDropdownItemsByCategory('SUB_ROLE', true, true);

	// Fetch position titles from database
	const { data: positionTitlesData, isLoading: isLoadingPositionTitles } = useGetPositionTitlesQuery();

	// Fetch company data when companyId is provided (for account managers)
	useGetCompanyByIdQuery(companyId || '', (data) => {
		if (data?.company && (!selectedCompany?.company?._id || selectedCompany?.company?._id !== data.company._id)) {
			// Convert GetCompanyByIdAPIResponse to MyCompanyType format
			setSelectedCompany({
				company: data.company,
			} as MyCompanyType);
		}
	});

	// Debug logging
	useEffect(() => {
		console.log('=== DEBUG: Pre-Placement Call Recording ===');
		console.log('companyId prop:', companyId);
		console.log('selectedCompany from context:', selectedCompany);
		console.log('selectedCompany?.company:', selectedCompany?.company);
		console.log('selectedCompany?.company?._id:', selectedCompany?.company?._id);
		console.log('selectedCompany?.company?.prePlacementCallRecording:', selectedCompany?.company?.prePlacementCallRecording);
		console.log('user?.registeredCompany:', user?.registeredCompany);
		console.log('user?.registeredCompany?._id:', user?.registeredCompany?._id);
		console.log('user?.registeredCompany?.prePlacementCallRecording:', user?.registeredCompany?.prePlacementCallRecording);
		console.log('selectedCompanyId (local state):', selectedCompanyId);
		console.log('Condition result:', !!(selectedCompany?.company?.prePlacementCallRecording || user?.registeredCompany?.prePlacementCallRecording));
		console.log('==========================================');
	}, [selectedCompany, user, selectedCompanyId, companyId]);



	// const [salaryValue, setSalaryValue] = useState<number[]>([8, 12]);
	const [, setExperienceValue] = useState<number[]>([0, 3]);
	const [positionDetail, setPositionDetail] = useState<RegisterPosition>({
		title: '' as PositionTitle,
		techStack: '' as TechStacksForPosition,
		requiredBTech: false,
		experience: {
			min: 0,
			max: 2,
		},
		goodToHaveSkillNames: [],
		requiredSkillNames: [],
		location: '',
		workLocations: [],
		positionType: '' as PositionType,
		salaryRange: {
			min: 0,
			max: 0,
		},
		vacancy: 1,
		numberOfRounds: 3,
		workingMode: WorkingMode.FULL_TIME,
		internshipSalary: {
			min: 5,
			max: 10,
		},
		internshipDuration: 0,
		hasBond: false,
		roundLabels: [] as SingleRoundLabel[],
	});

	// Find position title ID from name (enum value) for fetching tech stacks
	// We store the name (enum value) in positionDetail.title, but need the _id to fetch tech stacks
	const selectedPositionTitleId = React.useMemo(() => {
		if (!positionDetail.title || !positionTitlesData?.positionTitles) return null;
		const foundTitle = positionTitlesData.positionTitles.find(
			(pt: PositionTitleConfig) => pt.name === positionDetail.title
		);
		return foundTitle?._id || null;
	}, [positionTitlesData, positionDetail.title]);

	// Fetch tech stacks for selected position title
	const { data: techStacksForTitleData, isLoading: isLoadingTechStacksForTitle } = useGetTechStacksForPositionTitleQuery(
		selectedPositionTitleId || null,
		!!positionDetail.title && !!selectedPositionTitleId,
	);

	// Find tech stack ID from name (enum value) for fetching skills
	const selectedTechStackId = React.useMemo(() => {
		return techStacksForTitleData?.techStacks?.find(
			(ts: PositionTechStackConfig) => ts.name === positionDetail.techStack
		)?._id;
	}, [techStacksForTitleData, positionDetail.techStack]);

	// Fetch skills for selected tech stack
	const { data: skillsForTechStackData, isLoading: isLoadingSkillsForTechStack } = useGetSkillsForTechStackQuery(
		selectedTechStackId || null,
		!!positionDetail.techStack && !!selectedTechStackId,
	);

	const currentYear = moment().year();
	const textInputStyle = {
		color: 'black',
		backgroundColor: 'white',
		border: '1px solid #D1D5DB',
	};

	const { data: recruitersData } = useGetAllRecruitersOfCompany(companyId || '');

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();

		if (isAccountManagerLoggedIn && !formStep) {
			// Save position data including LMS TechStack fields
			const dataToSave = {
				...positionDetail,
				autoRecommendationEnable: autoRecommendationEnable,
				lmsTechStack: autoRecommendationEnable && lmsTechStacks.length > 0
					? lmsTechStacks.map(ts => ({
						techStack: String(ts.techStack), // Ensure techStack is always a string
						priority: ts.priority,
					}))
					: undefined,
				subRole: autoRecommendationEnable && subRoles.length > 0 ? subRoles : undefined,
				prepMaterial: prepMaterialLinks.filter(link => link.trim() !== ''),
				showPrepMaterialToStudent,
				showPrePlacementCallRecordingToStudent,
			};
			localStorage.setItem('saved-position-data', JSON.stringify(dataToSave));
			setFormStep(1);
		} else if (isAccountManagerLoggedIn && formStep === 1) {
			onOpen();
			setFormStep(2);
		} else if (!registerFn.isLoading && (formStep === 2 || isRecruiterLoggedIn)) {
			setFormStep(3);
			registerFn.mutate({
				...positionDetail,
				companyId: selectedCompanyId,
				autoRecommendationEnable: autoRecommendationEnable,
				lmsTechStack: autoRecommendationEnable && lmsTechStacks.length > 0
					? lmsTechStacks.map(ts => ({
						techStack: String(ts.techStack), // Ensure techStack is always a string
						priority: ts.priority,
					}))
					: undefined,
				subRole: autoRecommendationEnable && subRoles.length > 0 ? subRoles : undefined,
				prepMaterial: prepMaterialLinks.filter(link => link.trim() !== ''),
				showPrepMaterialToStudent,
				showPrePlacementCallRecordingToStudent,
			});
			onClose();
		}
	};

	const handleRecruiterEmailChange = (email: string) => {
		const existingRec = recruitersData?.recruiters?.find(
			r => r.email.toLowerCase().trim() === email.toLowerCase().trim(),
		);
		setExistingRec(existingRec);
		if (existingRec) {
			setDisableNewRecruiterFields(true);
		} else {
			setDisableNewRecruiterFields(false);
		}
		setPositionDetail(prev => ({
			...prev,
			newRecruiterDetails: {
				email: email.toLowerCase().trim(),
				name: existingRec?.name || '',
				phone: existingRec?.phone || '',
				hideRecommendations: existingRec?.flags?.hideRecommendations || false,
				recruiterDesignation: existingRec?.recruiterDesignation || '',
			},
		}));
	};

	const handleRecruiterDetailsChange = (change: Partial<RegisterPosition['newRecruiterDetails']>) => {
		setPositionDetail(prev => ({
			...prev,
			newRecruiterDetails: {
				email: prev.newRecruiterDetails?.email || '',
				name: prev.newRecruiterDetails?.name || '',
				phone: prev.newRecruiterDetails?.phone || '',
				hideRecommendations: prev.newRecruiterDetails?.hideRecommendations || false,
				recruiterDesignation: prev.newRecruiterDetails?.recruiterDesignation || '',
				...change,
			},
		}));
	};

	useEffect(() => {
		const localdata: any = localStorage.getItem('saved-position-data');
		if (localdata) {
			const savedPositionData: RegisterPosition = JSON.parse(localdata);

			// Set LMS Level TechStack toggle (default to true if not in saved data)
			setAutoRecommendationEnable(savedPositionData?.autoRecommendationEnable ?? true);
			// Set LMS TechStacks if they exist in saved data
			// Set subRoles if they exist in saved data
			if (savedPositionData?.subRole && Array.isArray(savedPositionData.subRole)) {
				setSubRoles(savedPositionData.subRole);
			}
			if (savedPositionData?.lmsTechStack && Array.isArray(savedPositionData.lmsTechStack)) {
				const mappedTechStacks: Array<{ techStack: string; priority: number }> = savedPositionData.lmsTechStack.map((ts: any) => ({
					techStack: String(ts.techStack || ts),
					priority: ts.priority ?? 0,
				}));
				setLmsTechStacks(mappedTechStacks);
			}
			// Restore prepMaterial and showPrepMaterialToStudent
			if (savedPositionData?.prepMaterial && Array.isArray(savedPositionData.prepMaterial)) {
				setPrepMaterialLinks(savedPositionData.prepMaterial.length > 0 ? savedPositionData.prepMaterial : ['']);
			}
			if (savedPositionData?.showPrepMaterialToStudent !== undefined) {
				setShowPrepMaterialToStudent(savedPositionData.showPrepMaterialToStudent);
			}
			if (savedPositionData?.showPrePlacementCallRecordingToStudent !== undefined) {
				setShowPrePlacementCallRecordingToStudent(savedPositionData.showPrePlacementCallRecordingToStudent);
			}

			setExperienceValue([savedPositionData?.experience?.min ?? 0, savedPositionData?.experience?.max ?? 2]);

			const positionData = {
				title: (savedPositionData?.title as PositionTitle) ?? ('' as PositionTitle),
				techStack: savedPositionData?.techStack ?? ('' as TechStacksForPosition),
				requiredBTech: savedPositionData?.requiredBTech ?? false,
				minGradYear: savedPositionData?.minGradYear,
				experience: {
					min: savedPositionData?.experience?.min ?? 0,
					max: savedPositionData?.experience?.max ?? 2,
				},
				goodToHaveSkillNames: savedPositionData?.goodToHaveSkillNames ?? [],
				requiredSkillNames: savedPositionData?.requiredSkillNames ?? [],
				location: savedPositionData?.location ?? '',
				workLocations: Array.isArray(savedPositionData?.workLocations) ? savedPositionData?.workLocations : [],

				positionType: savedPositionData?.positionType ?? ('' as PositionType),
				salaryRange: {
					min: savedPositionData?.salaryRange?.min ?? 0,
					max: savedPositionData?.salaryRange?.max ?? 20,
				},
				vacancy: savedPositionData?.vacancy ?? 1,
				numberOfRounds: savedPositionData?.numberOfRounds ?? 3,
				workingMode: savedPositionData?.workingMode ?? WorkingMode.FULL_TIME,
				internshipSalary: {
					min: 5,
					max: 10,
				},
				internshipDuration: 0,
				hasBond: savedPositionData?.hasBond,
				bondDetails: savedPositionData?.bondDetails,
				isFaceToFacePosition: savedPositionData?.isFaceToFacePosition,
				interviewFormat: savedPositionData?.interviewFormat,
				hasAssignmentRound: savedPositionData?.hasAssignmentRound,
				educationRequirements: savedPositionData?.educationRequirements,
			};

			if (isDecimal(savedPositionData?.experience?.min) || isDecimal(savedPositionData?.experience?.max)) {
				setSelectedExpInput({ value: 'months', label: 'Months' });
			}

			setExperienceInYears({
				min: 0,
				max: 0,
			});
			setExperienceInMonths({
				min: Math.ceil(savedPositionData?.experience?.min * 12),
				max: Math.ceil(savedPositionData?.experience?.max * 12),
			});

			if (savedPositionData?.workingMode === WorkingMode.INTERNSHIP) {
				setPositionDetail({
					...positionData,
					internshipDuration: savedPositionData?.internshipDuration || 0,
					internshipSalary: {
						min: savedPositionData?.internshipSalary?.min || 5,
						max: savedPositionData?.internshipSalary?.max || 10,
					},
				});
			} else if (savedPositionData?.workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME) {
				setPositionDetail({
					...positionData,
					internshipDuration: savedPositionData?.internshipDuration || 0,
					internshipSalary: {
						min: savedPositionData?.internshipSalary?.min || 5,
						max: savedPositionData?.internshipSalary?.max || 10,
					},
				});
			} else {
				setPositionDetail(positionData);
			}
		}
	}, []);

	useEffect(() => {
		const localdata: any = localStorage.getItem('duplicatePosition');
		if (localdata) {
			const duplicatePositionData: MyPositionType = JSON.parse(localdata);
			console.log('duplicatePositionData:', duplicatePositionData);

			// Set LMS Level TechStack toggle (default to true)
			setAutoRecommendationEnable(duplicatePositionData?.position?.autoRecommendationEnable ?? true);
			// Set LMS TechStacks if they exist
			// Set subRoles if they exist
			if (duplicatePositionData?.position?.subRole && Array.isArray(duplicatePositionData.position.subRole)) {
				setSubRoles(duplicatePositionData.position.subRole);
			}
			if (duplicatePositionData?.position?.lmsTechStack && Array.isArray(duplicatePositionData.position.lmsTechStack)) {
				setLmsTechStacks(duplicatePositionData.position.lmsTechStack.map((ts: any) => ({
					techStack: String(ts.techStack || ts),
					priority: ts.priority ?? 0,
				})));
			}

			setExperienceValue([
				duplicatePositionData?.position?.positionRequirement?.workExperience?.min ?? 0,
				duplicatePositionData?.position?.positionRequirement?.workExperience?.max ?? 2,
			]);

			const goodToHaveSkillNames =
				duplicatePositionData?.position?.positionRequirement?.goodToHaveskills?.map(skill => {
					let skillId = skill as unknown as string;
					return Object.keys(skillNameToIdMapping).find(key => {
						return skillNameToIdMapping[key] === skillId;
					});
				}) ?? [];

			const positionData = {
				title: (duplicatePositionData?.position?.title as PositionTitle) ?? ('' as PositionTitle),
				techStack: duplicatePositionData?.position?.techStack ?? ('' as TechStacksForPosition),
				requiredBTech: duplicatePositionData?.position?.requiredBTech ?? false,
				minGradYear: duplicatePositionData?.position?.positionRequirement?.minGradYear,
				experience: {
					min: duplicatePositionData?.position?.positionRequirement?.workExperience?.min ?? 0,
					max: duplicatePositionData?.position?.positionRequirement?.workExperience?.max ?? 2,
				},
				goodToHaveSkillNames: (goodToHaveSkillNames as unknown as string[]) ?? [],
				requiredSkillNames:
					duplicatePositionData?.position?.positionRequirement?.skillThresholds?.map(el => el?.displaySkill) ?? [],
				location: duplicatePositionData?.position?.workLocation ?? '',
				workLocations: Array.isArray(duplicatePositionData?.position?.workLocations) ? duplicatePositionData?.position?.workLocations : [],
				positionType: duplicatePositionData?.position?.positionType ?? ('' as PositionType),
				salaryRange: {
					min: duplicatePositionData?.position?.salary?.min ?? 0,
					max: duplicatePositionData?.position?.salary?.max ?? 20,
				},
				vacancy: duplicatePositionData?.position?.vacancy ?? 1,
				numberOfRounds: duplicatePositionData?.position?.numberOfRounds ?? 3,
				workingMode: duplicatePositionData?.position?.workingMode ?? WorkingMode.FULL_TIME,
				internshipSalary: {
					min: 5,
					max: 10,
				},
				internshipDuration: 0,
				hasBond: duplicatePositionData?.position?.hasBond,
				bondDetails: duplicatePositionData?.position?.bondDetails,
				isFaceToFacePosition: duplicatePositionData?.position?.isFaceToFacePosition,
				interviewFormat: duplicatePositionData?.position?.interviewFormat,
				hasAssignmentRound: duplicatePositionData?.position?.hasAssignmentRound,
				educationRequirements: duplicatePositionData?.position?.positionRequirement?.educationRequirements,
			};

			if (
				isDecimal(duplicatePositionData?.position?.positionRequirement?.workExperience?.min) ||
				isDecimal(duplicatePositionData?.position?.positionRequirement?.workExperience?.max)
			) {
				setSelectedExpInput({ value: 'months', label: 'Months' });
			}

			setExperienceInYears({
				min: 0,
				max: 0,
			});
			setExperienceInMonths({
				min: Math.ceil(duplicatePositionData?.position?.positionRequirement?.workExperience?.min * 12),
				max: Math.ceil(duplicatePositionData?.position?.positionRequirement?.workExperience?.max * 12),
			});

			if (duplicatePositionData?.position?.workingMode === WorkingMode.INTERNSHIP) {
				setPositionDetail({
					...positionData,
					internshipDuration: duplicatePositionData?.position?.internshipDuration || 0,
					internshipSalary: {
						min: duplicatePositionData?.position?.internshipSalary?.min || 5,
						max: duplicatePositionData?.position?.internshipSalary?.max || 10,
					},
				});
			} else if (duplicatePositionData?.position?.workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME) {
				setPositionDetail({
					...positionData,
					internshipDuration: duplicatePositionData?.position?.internshipDuration || 0,
					internshipSalary: {
						min: duplicatePositionData?.position?.internshipSalary?.min || 5,
						max: duplicatePositionData?.position?.internshipSalary?.max || 10,
					},
				});
			} else {
				setPositionDetail(positionData);
			}
		}

		return () => {
			localStorage.removeItem('duplicatePosition');
		};
	}, []);

	useEffect(() => {
		const types = Object.values(PositionType).map(type => ({ label: type, value: type }));
		setPositionTypes(types);

		// Use database position titles if available, otherwise fallback to enum
		if (positionTitlesData?.positionTitles) {
			const titles = positionTitlesData.positionTitles.map((title: PositionTitleConfig) => ({
				label: title.label || title.name,
				value: title.name, // Use name (enum value) to store as string, not _id
			}));
			setPositionTitles(titles);
		} else {
			// No fallback - only use database values
			setPositionTitles([]);
		}
	}, [positionTitlesData]);

	useEffect(() => {
		// Use database tech stacks if available, otherwise fallback to enum mapping
		if (techStacksForTitleData?.techStacks && positionDetail.title) {
			const stacks = techStacksForTitleData.techStacks.map((ts: PositionTechStackConfig) => ({
				label: ts.label || ts.name,
				value: ts.name, // Use name (enum value) for backward compatibility with backend
			}));
			setTechStacks(stacks);
		} else {
			// No fallback - only use database values
			setTechStacks([]);
		}
	}, [positionDetail.title, techStacksForTitleData]);

	// Separate skills into required (all skills) and good to have (only soft skills)
	const requiredSkillsList = React.useMemo<Dropdown[]>(() => {
		// Use database skills if available, otherwise fallback to enum mapping
		if (skillsForTechStackData?.skills && positionDetail.techStack) {
			const skills = skillsForTechStackData.skills.map((skill: PositionSkill) => ({
				label: skill.name,
				value: skill.name, // Use skill name (matching existing skillNameToIdMapping)
			}));
			return skills;
		}
		// No fallback - only use database values
		return [];
	}, [positionDetail.techStack, skillsForTechStackData]);

	// Good to have skills: only soft skills (isSoftSkill === true)
	const goodToHaveSkillsList = React.useMemo<Dropdown[]>(() => {
		// Use database skills if available, filter by isSoftSkill
		if (skillsForTechStackData?.skills && positionDetail.techStack) {
			const softSkills = skillsForTechStackData.skills
				.filter((skill: PositionSkill) => skill.isSoftSkill === true)
				.map((skill: PositionSkill) => ({
					label: skill.name,
					value: skill.name,
				}));
			return softSkills;
		}
		// Fallback: if using enum-based, we don't have isSoftSkill info, so return empty
		// Admin should configure skills in database for proper filtering
		return [];
	}, [positionDetail.techStack, skillsForTechStackData]);

	useEffect(() => {
		// Set all skills list for backward compatibility (used in some places)
		setSkillsList(requiredSkillsList);
	}, [requiredSkillsList]);

	useEffect(() => {
		setSelectedCompanyId(companyId || '');
	}, [companyId]);

	const expRange = {
		minYear: Math.floor(positionDetail?.experience?.min),
		minMonth: Math.ceil(positionDetail?.experience?.min * 12) % 12,
		maxYear: Math.floor(positionDetail?.experience?.max),
		maxMonth: Math.ceil(positionDetail?.experience?.max * 12) % 12,
	};

	const labelStyle = `dark:text-white text-[#111928]`;

	const isDisabledCTA =
		expError ||
		(!user?.phone && !(positionDetail?.phone?.length === 10)) ||
		!positionDetail.title ||
		registerFn.isLoading ||
		positionDetail.requiredSkillNames.length < 2 ||
		!positionDetail.salaryRange ||
		// !positionDetail.location ||
		!positionDetail.workLocations ||
		!positionDetail.positionType ||
		!positionDetail.vacancy ||
		!(positionDetail.vacancy > 0) ||
		(!positionDetail.salaryRange.min &&
			!positionDetail.salaryRange.max &&
			[WorkingMode.FULL_TIME, WorkingMode.INTERNSHIP_TO_FULL_TIME].includes(positionDetail.workingMode)) ||
		!positionDetail.workingMode ||
		!positionDetail.numberOfRounds ||
		!(positionDetail.numberOfRounds > 0) ||
		!positionDetail.techStack ||
		// !(
		// 	!positionDetail?.isFaceToFacePosition ||
		// 	(positionDetail?.isFaceToFacePosition && positionDetail.interviewFormat)
		// ) ||
		(formStep === 1 &&
			(!positionDetail.newRecruiterDetails?.name ||
				!positionDetail.newRecruiterDetails?.email ||
				!validateEmailAddress(positionDetail.newRecruiterDetails?.email) ||
				!positionDetail.newRecruiterDetails.phone)) ||
		(!!positionDetail.minGradYear && !_.inRange(positionDetail.minGradYear, 1950, currentYear + 1)) ||
		((positionDetail?.workingMode === WorkingMode.INTERNSHIP ||
			positionDetail?.workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME) &&
			!positionDetail?.internshipSalary) ||
		((positionDetail?.workingMode === WorkingMode.INTERNSHIP ||
			positionDetail?.workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME) &&
			!(positionDetail?.internshipDuration && positionDetail?.internshipDuration > 0));

	const validatePositionTitle = () => {
		return hasTouchedContinue && !positionDetail?.title;
	};

	const validatePositionTechstack = () => {
		return hasTouchedContinue && !positionDetail?.techStack;
	};

	const validatePositionNumberOfRounds = () => {
		return hasTouchedContinue && !positionDetail?.numberOfRounds;
	};

	const validatePositionRequiredSkillNames = () => {
		return hasTouchedContinue && !(positionDetail?.requiredSkillNames?.length > 1);
	};

	const validatePositionWorkLocationAndWorkType = () => {
		// hasTouchedContinue && (!positionDetail?.location || !positionDetail.positionType)
		return hasTouchedContinue && (!positionDetail?.workLocations || !positionDetail.positionType);
	};

	const validateUserPhone = () => {
		return hasTouchedContinue && !positionDetail?.phone && !validatePhoneNumber(positionDetail?.phone || '');
	};

	return (
		<form className="flex font-inter flex-col gap-4 mt-[20px]" onSubmit={handleSubmit}>
			{!formStep ? (
				<>
					{/* Job Title */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="title" value="Job title" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<Select
							required
							options={positionTitles}
							menuPlacement={'auto'}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							isLoading={isLoadingPositionTitles}
							value={positionTitles.find(title => title.value === positionDetail?.title)}
							onChange={(val: SingleValue<Dropdown>) => {
								if (val?.value) {
									setPositionDetail({
										...positionDetail,
										techStack: '' as TechStacksForPosition,
										requiredSkillNames: [],
										goodToHaveSkillNames: [],
										title: val.value as PositionTitle, // val.value is already the name (enum value)
									});
								}
							}}
							placeholder="SDE 3"
						/>

						{validatePositionTitle() ? (
							<span className="text-red-600 text-sm">Please select a position title</span>
						) : null}
					</div>

					{/* Tech stack for position */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="techStack" value="Tech Stack" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<Select
							required
							options={techStacks}
							menuPlacement={'auto'}
							isLoading={isLoadingTechStacksForTitle}
							value={techStacks?.find(techStack => techStack.value === positionDetail?.techStack) ?? null}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							onChange={(val: SingleValue<Dropdown>) => {
								if (val?.value) {
									setPositionDetail({
										...positionDetail,
										requiredSkillNames: [],
										goodToHaveSkillNames: [],
										techStack: val.value as TechStacksForPosition, // val.value is already the name (enum value)
									});
								}
							}}
							placeholder="MERN"
						/>

						{validatePositionTechstack() ? (
							<span className="text-red-600 text-sm">Please select a relevant techstack</span>
						) : null}
					</div>

					{/* LMS Level TechStack Toggle */}
					<div className="py-2 flex items-center gap-2">
						<div className="mb-1">
							<Label className="text-base">Auto Recommendation Enabled?</Label>
						</div>
						<ToggleSwitch
							checked={autoRecommendationEnable || false}
							onChange={e => {
								setAutoRecommendationEnable(e);
								if (!e) {
									setLmsTechStacks([]);
								}
							}}
						/>
					</div>

					{/* LMS TechStack Multi-Select */}
					{autoRecommendationEnable && (
						<div className="mt-3">
							<div>
								<Label>LMS TechStack</Label>
								<span className="text-red-600">*</span>
							</div>
							<Select
								menuPlacement={'auto'}
								classNamePrefix="my-select-plans"
								isMulti
								closeMenuOnSelect={false}
								isLoading={isLoadingLMSTechStacks}
								options={lmsTechStacksData?.lmsTechStacks
									?.filter((techStack: LMSTechStack) => techStack._id && techStack.isActive !== false)
									.map((techStack: LMSTechStack) => ({
										label: techStack.name,
										value: techStack._id!,
									})) || []}
								value={lmsTechStacksData?.lmsTechStacks
									?.filter((techStack: LMSTechStack) => techStack._id)
									.map((techStack: LMSTechStack) => ({ label: techStack.name, value: String(techStack._id!) }))
									.filter((option: { label: string; value: string }) => lmsTechStacks.some(ts => String(ts.techStack) === option.value))}
								onChange={(vals: MultiValue<{ label: string; value: string }>) => {
									const selectedValues = vals || [];
									// Update lmsTechStacks array, auto-assigning priority based on index
									// Priority is auto-assigned (0, 1, 2, etc.) - no user input needed
									const updated = selectedValues.map((val, index) => {
										const existing = lmsTechStacks.find(ts => String(ts.techStack) === String(val.value));
										return {
											techStack: String(val.value), // Ensure techStack is always a string (ObjectId)
											priority: existing?.priority ?? index, // Auto-assign priority based on selection order
										};
									});
									setLmsTechStacks(updated);
								}}
								placeholder="Select LMS TechStack(s)"
							/>
							{lmsTechStacks.length > 0 && (
								<div className="mt-2 text-sm text-gray-600">
									Selected: {lmsTechStacks.map((ts, idx) => {
										const techStackName = lmsTechStacksData?.lmsTechStacks?.find((t: LMSTechStack) => t._id === ts.techStack)?.name || '';
										return techStackName;
									}).join(', ')}
								</div>
							)}
							{autoRecommendationEnable && lmsTechStacks.length === 0 && hasTouchedContinue && (
								<span className="text-red-600 text-sm">Please select at least one LMS TechStack</span>
							)}
						</div>
					)}

					{/* SubRole Multi-Select */}
					{autoRecommendationEnable && (
						<div className="mt-3">
							<div>
								<Label>SubRole</Label>
								<span className="text-red-600">*</span>
							</div>
							<Select
								menuPlacement={'auto'}
								classNamePrefix="my-select-plans"
								isMulti
								closeMenuOnSelect={false}
								options={subRoleDataForCreate?.lmsTechStacks?.map(item => ({
									label: item.label || item.name, // Use label for display, fallback to name
									value: item.name, // Store name (string value) in database
								})) || []}
								value={subRoleDataForCreate?.lmsTechStacks
									?.filter(item => subRoles.includes(item.name))
									.map(item => ({
										label: item.label || item.name, // Display label
										value: item.name // Value is name (string value)
									})) || []}
								onChange={(vals: MultiValue<{ label: string; value: string }>) => {
									const selectedValues = vals || [];
									setSubRoles(selectedValues.map(val => val.value));
								}}
							/>
							{autoRecommendationEnable && subRoles.length === 0 && hasTouchedContinue && (
								<span className="text-red-600 text-sm">Please select at least one SubRole</span>
							)}
						</div>
					)}

					{/* Job type */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="title" value="Job type" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>

						<fieldset
							className="flex flex-wrap max-w-md gap-4"
							onChange={e => {
								const target = e.target as HTMLInputElement;
								if (target?.value) {
									setPositionDetail({ ...positionDetail, workingMode: target?.value as WorkingMode });
								}
							}}
						>
							{WorkingMode &&
								Object.values(WorkingMode).map((mode: string, i: number) => (
									<div key={i} className="flex items-center gap-2">
										<Radio
											className="text-[#7777E7] focus:ring-[#7777E7]"
											id={mode}
											name="workingMode"
											value={mode}
											checked={positionDetail?.workingMode === mode}
										/>
										<Label htmlFor={mode}>{camelCaseToSpace(mode)}</Label>
									</div>
								))}
						</fieldset>
					</div>

					{/* Number of rounds */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="title" value="Number of rounds" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>

						<div className="flex items-center gap-[8px]">
							<Button
								className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
								color="light"
								disabled={positionDetail.numberOfRounds <= 1}
								onClick={() => {
									setPositionDetail({ ...positionDetail, numberOfRounds: --positionDetail.numberOfRounds });
								}}
							>
								<IoMdRemove className="w-[24px] h-[24px]" />
							</Button>

							<TextInput
								min={1}
								max={10}
								onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
								type="number"
								placeholder="2"
								name="numberOfRounds"
								value={positionDetail.numberOfRounds}
								onChange={event => {
									if (+event?.target?.value <= 10)
										setPositionDetail({ ...positionDetail, numberOfRounds: +event?.target?.value });
								}}
								className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow"
							/>

							<Button
								className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
								color="light"
								disabled={positionDetail.numberOfRounds >= 10}
								onClick={() => {
									setPositionDetail({ ...positionDetail, numberOfRounds: ++positionDetail.numberOfRounds });
								}}
							>
								<IoMdAdd className="w-[24px] h-[24px]" />
							</Button>
						</div>

						{validatePositionNumberOfRounds() ? (
							<span className="text-red-600 text-sm">Please select number of rounds</span>
						) : null}
					</div>
					<SelectRoundType
						onChange={(roundLabels: SingleRoundLabel[]) => {
							roundLabels = roundLabels?.filter(roundLabel => roundLabel.type);
							console.log('final round labels:', roundLabels);
							setPositionDetail(prev => ({ ...prev, roundLabels }));
						}}
						value={positionDetail.roundLabels || []}
						numberOfRounds={positionDetail?.numberOfRounds}
					/>

					{/* Minimum Grad Year */}
					{isAccountManagerLoggedIn ? (
						<div>
							<div className="mb-2 block">
								<Label htmlFor="minGradYear" value="Minimum Grad Year" className={`${labelStyle}`} />
							</div>

							<div className="flex items-center gap-[8px]">
								<Button
									className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
									color="light"
									disabled={(positionDetail.minGradYear || currentYear) <= 1950}
									onClick={() => {
										setPositionDetail({
											...positionDetail,
											minGradYear: (positionDetail.minGradYear || currentYear) - 1,
										});
									}}
								>
									<IoMdRemove className="w-[24px] h-[24px]" />
								</Button>

								<TextInput
									min={1950}
									max={currentYear}
									onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
									type="number"
									placeholder={currentYear.toString()}
									name="minGradYear"
									value={positionDetail.minGradYear}
									onChange={event => {
										if (+event?.target?.value <= currentYear)
											setPositionDetail({ ...positionDetail, minGradYear: +event?.target?.value });
									}}
									className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow"
								/>

								<Button
									className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
									color="light"
									disabled={(positionDetail.minGradYear || currentYear) >= currentYear}
									onClick={() => {
										setPositionDetail({
											...positionDetail,
											minGradYear: (positionDetail.minGradYear || currentYear) + 1,
										});
									}}
								>
									<IoMdAdd className="w-[24px] h-[24px]" />
								</Button>
							</div>
						</div>
					) : null}

					{/* Education Requirement */}
					<div>
						<div className="mb-2 block">
							<Label
								htmlFor="title"
								value="Educational qualifications required (optional)"
								className={`${labelStyle}`}
							/>
						</div>
						<Select
							menuPlacement={'auto'}
							styles={{
								multiValue: (baseStyles, state) => ({
									...baseStyles,
									backgroundColor: '#111E2C',
								}),
							}}
							closeMenuOnSelect={false}
							blurInputOnSelect={false}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							options={PositionEducationRequirementTypeOptions}
							value={positionDetail?.educationRequirements?.map(degree => ({
								label: degree,
								value: degree,
							}))}
							isMulti={true}
							onChange={(value: MultiValue<Dropdown>) => {
								const selectedDegrees = value.map(item => item.value);
								setPositionDetail({
									...positionDetail,
									educationRequirements: selectedDegrees as PositionEducationRequirementType[],
								});
							}}
							placeholder="Any Degree"
						/>
					</div>

					{isAccountManagerLoggedIn ? (
						<div className="flex items-center flex-wrap gap-[20%]">
							{/* <div>
								<div className="mb-2 block">
									<Label htmlFor="techOnly" value="Required B.Tech/BE only" className={`${labelStyle}`} />
								</div>

								<ToggleSwitch
									id="techOnly"
									checked={positionDetail.requiredBTech || false}
									onChange={e => {
										console.log('289');
										setPositionDetail(prev => ({ ...prev, requiredBTech: e }));
									}}
								/>
							</div> */}
							<div>
								<div className="mb-2 block">
									<Label htmlFor="onlyMasai" value="Only for Masai One Students?" className={`${labelStyle}`} />
								</div>

								<ToggleSwitch
									id="onlyMasai"
									checked={positionDetail.onlyForMasaiOneStudents || false}
									onChange={e => {
										console.log('474');
										setPositionDetail(prev => ({ ...prev, onlyForMasaiOneStudents: e }));
									}}
								/>
							</div>
						</div>
					) : null}

					<div className="flex items-center flex-wrap gap-[20%]">
						{isAccountManagerLoggedIn ? (
							<div>
								<div className="mb-2 block">
									<Label htmlFor="hasAssignmentRound" value="Has Assignment Round?" className={`${labelStyle}`} />
								</div>

								<ToggleSwitch
									id="hasAssignmentRound"
									checked={positionDetail?.hasAssignmentRound || false}
									onChange={e => {
										console.log('504');
										setPositionDetail(prev => ({ ...prev, hasAssignmentRound: e }));
									}}
								/>
							</div>
						) : null}
					</div>

					{/* For IsFace To Face Position */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="title" value="Interview format" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>

						<fieldset
							className="flex flex-wrap max-w-md gap-8"
							onChange={e => {
								const target = e.target as HTMLInputElement;
								console.log('target?.value:', target?.value);
								if (target?.value) {
									setPositionDetail(prev => ({
										...prev,
										isFaceToFacePosition: target.value === InterviewFormatType.INPERSON,
									}));
								}
							}}
						>
							<div className="flex items-center gap-2">
								<Radio
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={InterviewFormatType.ONLINE}
									name="interviewFormat"
									value={InterviewFormatType.ONLINE}
									checked={positionDetail?.isFaceToFacePosition ? false : true}
								/>
								<Label htmlFor={InterviewFormatType.ONLINE}>Online</Label>
							</div>

							<div className="flex items-center gap-2">
								<Radio
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={InterviewFormatType.INPERSON}
									name="interviewFormat"
									value={InterviewFormatType.INPERSON}
									checked={positionDetail?.isFaceToFacePosition ? true : false}
								/>
								<Label htmlFor={InterviewFormatType.INPERSON}>In-person</Label>
							</div>
						</fieldset>
					</div>

					{/* For Bond */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="title" value="Includes Bond Agreement" className={`${labelStyle}`} />
						</div>

						<fieldset
							className="flex flex-wrap max-w-md gap-8"
							onChange={e => {
								const target = e.target as HTMLInputElement;
								if (target?.value) {
									setPositionDetail(prev => ({ ...prev, hasBond: target.value === 'true', bondDetails: '' }));
								}
							}}
						>
							<div className="flex items-center gap-2">
								<Radio
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'yes'}
									name="hasBond"
									value={'true'}
									checked={positionDetail?.hasBond === true}
								/>
								<Label htmlFor={'yes'}>Yes</Label>
							</div>

							<div className="flex items-center gap-2">
								<Radio
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'no'}
									name="hasBond"
									value={'false'}
									checked={positionDetail?.hasBond === false}
								/>
								<Label htmlFor={'no'}>No</Label>
							</div>
						</fieldset>
					</div>

					{/* For Bond Details */}
					{positionDetail?.hasBond ? (
						<div>
							<div className="mb-2 flex justify-between items-end">
								<div>
									<Label className={`${labelStyle}`} htmlFor="message" value="Bond Agreement Details" />
								</div>

								<span className="italic text-xs text-[#6B7280] ">
									{(positionDetail?.bondDetails?.length || 0) + ' / ' + 1000}
								</span>
							</div>

							<Textarea
								style={{
									fontFamily: "'Inter', sans-serif",
									borderRadius: '8px',
									backgroundColor: '#f9fafb',
								}}
								id="message"
								name="message"
								maxLength={1000}
								value={positionDetail?.bondDetails}
								placeholder="Write your message here..."
								className="border bg-white dark:text-white dark:border-[#4B5563] min-h-[100px] dark:bg-[#374151] focus:border-transparent focus:!ring-[1px] focus:!ring-[#7777E7]"
								onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
									setPositionDetail({ ...positionDetail, bondDetails: e.target.value })
								}
							/>
						</div>
					) : null}

					{/* Must have skills */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="title" value="Must have skills (min 2 skills required)" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<Select
							menuPlacement={'auto'}
							styles={{
								multiValue: (baseStyles, state) => ({
									...baseStyles,
									backgroundColor: '#111E2C',
								}),
							}}
							closeMenuOnSelect={false}
							blurInputOnSelect={false}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							isLoading={isLoadingSkillsForTechStack}
							options={requiredSkillsList.filter((skill: Dropdown) => !positionDetail.goodToHaveSkillNames.includes(skill.value))}
							value={positionDetail?.requiredSkillNames?.map(skillthreshold => ({
								label: skillthreshold,
								value: skillthreshold,
							}))}
							isMulti={true}
							onChange={(value: MultiValue<Dropdown>) => {
								const selectedSkills = value.map(item => item.value);
								setPositionDetail({ ...positionDetail, requiredSkillNames: selectedSkills });
							}}
							placeholder="Django, React, Python, Java"
						/>

						{validatePositionRequiredSkillNames() ? (
							<span className="text-red-600 text-sm">Please select min of 2 skills</span>
						) : null}
					</div>

					{/* Good to have skills - Only soft skills */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="title" value="Good to have skills" className={`${labelStyle}`} />						</div>
						<Select
							menuPlacement={'auto'}
							closeMenuOnSelect={false}
							blurInputOnSelect={false}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							isLoading={isLoadingSkillsForTechStack}
							options={goodToHaveSkillsList.filter((skill: Dropdown) => !positionDetail.requiredSkillNames.includes(skill.value))}
							value={positionDetail?.goodToHaveSkillNames.map(el => {
								return { label: el, value: el };
							})}
							isMulti={true}
							styles={{
								multiValue: (baseStyles, state) => ({
									...baseStyles,
									backgroundColor: '#111E2C',
								}),
							}}
							placeholder="Communication, Java"
							onChange={(value: MultiValue<Dropdown>) => {
								const selectedSkills = value.map(item => item.value);
								setPositionDetail({ ...positionDetail, goodToHaveSkillNames: selectedSkills });
							}}
						/>
					</div>

					{/* Experience Required */}
					<div>
						<div className="flex w-full justify-between">
							<div className="mb-1 block">
								<Label htmlFor="title" value="Experience required (in years, months)" className={`${labelStyle}`} />
								<span className="text-red-600 pl-1">*</span>
							</div>

							<Select
								menuPlacement={'auto'}
								classNamePrefix="my-select-plans"
								className="single-value-select-tag"
								options={[
									{ value: 'months', label: 'Months' },
									{ value: 'years', label: 'Years' },
								]}
								value={selectedExpInput}
								onChange={(data: SingleValue<Dropdown>) => {
									setPositionDetail({
										...positionDetail,
										experience: { min: 0, max: 0 },
									});
									setExperienceInMonths({ min: 0, max: 0 });
									setSelectedExpInput(data);
								}}
								placeholder=""
							/>
						</div>

						{positionDetail?.experience?.min || positionDetail?.experience?.max ? (
							<div className="text-xs">
								Preview Exp. Range - ({expRange?.minYear} {expRange?.minYear > 1 ? 'Years ' : 'Year '}
								{expRange?.minMonth} {expRange?.minMonth > 1 ? 'Months ' : 'Month '}
								{'to '}
								{expRange?.maxYear} {expRange?.maxYear > 1 ? 'Years ' : 'Year '}
								{expRange?.maxMonth} {expRange?.maxMonth > 1 ? 'Months' : 'Month'})
							</div>
						) : null}

						<div className="flex items-center gap-2 mt-2">
							<div className="flex items-center gap-2">
								{selectedExpInput?.value === 'months' ? (
									<TextInput
										placeholder="Min Months"
										type="number"
										max={100}
										pattern="\d*"
										step={1}
										value={experienceInMonths.min}
										onChange={e => {
											const selectedValue = Number(e?.target?.value.replace(/\D/g, '') || 0);
											setExperienceInMonths({
												min: Number(selectedValue),
												max: selectedValue > experienceInMonths?.max ? selectedValue : experienceInMonths?.max,
											});

											const calcValue = Math.floor(+(selectedValue / 12 + experienceInYears?.min) * 100) / 100;

											setPositionDetail({
												...positionDetail,
												experience: {
													min: calcValue,
													max:
														selectedValue > experienceInMonths?.max ? calcValue : positionDetail?.experience?.max || 0,
												},
											});
										}}
									/>
								) : (
									<TextInput
										placeholder="Min Years"
										type="number"
										max={100}
										pattern="\d*"
										step={1}
										value={positionDetail.experience.min}
										onChange={e => {
											const selectedValue = +e.target.value.replace(/\D/g, '');
											setPositionDetail({
												...positionDetail,
												experience: {
													max:
														selectedValue > positionDetail.experience.max
															? selectedValue
															: positionDetail.experience.max,
													min: selectedValue,
												},
											});
										}}
									/>
								)}
								{/*<Select
									menuPlacement={'auto'}
									classNamePrefix="my-select-plans"
									className="single-value-select-tag hideIndicator min-w-[50px]"
									options={generateRangeValuesArray(81, 0)}
									value={{
										label: String(experienceInYears?.min),
										value: String(experienceInYears?.min),
									}}
									onChange={(data: SingleValue<Dropdown>) => {
										const selectedValue = Number(data?.value || 0);

										setExperienceInYears({
											min: selectedValue,
											max: experienceInYears.max < selectedValue ? selectedValue : experienceInYears.max,
										});

										const calcValue = Math.floor(+(experienceInMonths?.min / 12 + selectedValue) * 100) / 100;
										const calcMaxValue =
											experienceInYears.max < selectedValue
												? Math.floor(+(experienceInMonths?.max / 12 + selectedValue) * 100) / 100
												: positionDetail.experience.max;

										setPositionDetail({
											...positionDetail,
											experience: {
												min: calcValue,
												max: calcMaxValue || 0,
											},
										});
									}}
									placeholder="Min Years"
								/>
								 Y
								<Select
									menuPlacement={'auto'}
									styles={{
										multiValue: (baseStyles, state) => ({
											...baseStyles,
											backgroundColor: '#111E2C',
										}),
									}}
									classNamePrefix="my-select-plans"
									className="single-value-select-tag hideIndicator min-w-[50px]"
									options={generateRangeValuesArray(12, 0)}
									value={{
										label: String(experienceInMonths?.min),
										value: String(experienceInMonths?.min),
									}}
									onChange={(data: SingleValue<Dropdown>) => {
										const selectedValue = Number(data?.value || 0);
										setExperienceInMonths({
											min: Number(selectedValue),
											max: experienceInMonths?.max,
										});

										const calcValue = Math.floor(+(selectedValue / 12 + experienceInYears?.min) * 100) / 100;

										setPositionDetail({
											...positionDetail,
											experience: {
												min: calcValue,
												max: positionDetail?.experience?.max || 0,
											},
										});
									}}
									placeholder="0"
								/>
								M */}
							</div>

							<div className="h-[1px] w-[14px] bg-[#C7C9D9]"></div>

							<div className="flex items-center gap-2">
								{selectedExpInput?.value === 'months' ? (
									<TextInput
										placeholder="Max Months"
										type="number"
										max={100}
										pattern="\d*"
										step={1}
										value={experienceInMonths.max}
										onChange={e => {
											const selectedValue = Number(e?.target?.value.replace(/\D/g, '') || 0);
											if (selectedValue < experienceInMonths?.min) {
												setExpError(true);
											} else {
												setExpError(false);
											}

											setExperienceInMonths({
												max: Number(selectedValue),
												min: experienceInMonths?.min,
											});

											const calcValue = Math.floor(+(selectedValue / 12 + experienceInYears?.max) * 100) / 100;

											setPositionDetail({
												...positionDetail,
												experience: {
													max: calcValue,
													min: positionDetail?.experience?.min || 0,
												},
											});
										}}
									/>
								) : (
									<TextInput
										placeholder="Max Years"
										type="number"
										max={100}
										pattern="\d*"
										step={1}
										value={positionDetail.experience.max}
										onChange={e => {
											const selectedValue = +e.target.value.replace(/\D/g, '');

											if (selectedValue < positionDetail.experience.min) {
												setExpError(true);
											} else {
												setExpError(false);
											}

											setPositionDetail({
												...positionDetail,
												experience: {
													...positionDetail.experience,
													max: selectedValue,
												},
											});
										}}
									/>
								)}

								{/* <Select
									menuPlacement={'auto'}
									classNamePrefix="my-select-plans"
									className="single-value-select-tag hideIndicator min-w-[50px]"
									options={generateRangeValuesArray(81, 0).filter(el => +el.value >= +(experienceInYears?.min ?? 0))}
									value={{
										label: String(experienceInYears?.max),
										value: String(experienceInYears?.max),
									}}
									onChange={(data: SingleValue<Dropdown>) => {
										const selectedValue = Number(data?.value || 0);
										setExperienceInYears({
											max: Number(selectedValue),
											min: experienceInYears?.min,
										});

										const calcValue = Math.floor(+(experienceInMonths?.max / 12 + selectedValue) * 100) / 100;

										setPositionDetail({
											...positionDetail,
											experience: {
												max: calcValue,
												min: positionDetail?.experience?.min || 0,
											},
										});
									}}
									placeholder="0"
								/>
								Y<div className="h-[1px] w-[14px] bg-[#C7C9D9]"></div>
								<Select
									menuPlacement={'auto'}
									styles={{
										multiValue: (baseStyles, state) => ({
											...baseStyles,
											backgroundColor: '#111E2C',
										}),
									}}
									classNamePrefix="my-select-plans"
									className="single-value-select-tag hideIndicator min-w-[50px]"
									options={generateRangeValuesArray(12, 0)}
									value={{
										label: String(experienceInMonths?.max),
										value: String(experienceInMonths?.max),
									}}
									onChange={(data: SingleValue<Dropdown>) => {
										const selectedValue = Number(data?.value || 0);
										setExperienceInMonths({
											max: Number(selectedValue),
											min: experienceInMonths?.min,
										});

										const calcValue = Math.floor(+(selectedValue / 12 + experienceInYears?.max) * 100) / 100;

										setPositionDetail({
											...positionDetail,
											experience: {
												max: calcValue,
												min: positionDetail?.experience?.min || 0,
											},
										});
									}}
									placeholder="0"
								/>
								M */}
							</div>
						</div>

						{expError ? (
							<div className="mt-2 text-sm text-red-600 dark:text-red-500">Max Exp can't be less then Min Exp</div>
						) : null}
					</div>

					{/* InternShip Duration */}
					{positionDetail?.workingMode &&
						(positionDetail?.workingMode === WorkingMode.INTERNSHIP ||
							positionDetail?.workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME) ? (
						<div>
							<div className="mb-2 block">
								<Label htmlFor="title" value="Internship Duration (in months)" className={`${labelStyle}`} />
								<span className="text-red-600 pl-1">*</span>
							</div>
							<div className="flex items-center gap-[8px]">
								<Button
									className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
									color="light"
									disabled={
										(positionDetail.internshipDuration !== undefined && positionDetail.internshipDuration <= 1) ||
										positionDetail.internshipDuration === undefined
									}
									onClick={() => {
										setPositionDetail({
											...positionDetail,
											internshipDuration:
												positionDetail.internshipDuration !== undefined ? --positionDetail.internshipDuration : 1,
										});
									}}
								>
									<IoMdRemove className="w-[24px] h-[24px]" />
								</Button>

								<TextInput
									min={1}
									max={12}
									onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
									type="number"
									placeholder="2"
									name="internshipDuration"
									value={positionDetail.internshipDuration || 0}
									onChange={event => {
										if (+event?.target?.value <= 12)
											setPositionDetail({ ...positionDetail, internshipDuration: +(event?.target?.value || 0) });
									}}
									className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow"
								/>

								<Button
									className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
									color="light"
									disabled={positionDetail.internshipDuration !== undefined && positionDetail.internshipDuration >= 12}
									onClick={() => {
										setPositionDetail({
											...positionDetail,
											internshipDuration:
												positionDetail.internshipDuration !== undefined ? ++positionDetail.internshipDuration : 1,
										});
									}}
								>
									<IoMdAdd className="w-[24px] h-[24px]" />
								</Button>
							</div>
						</div>
					) : null}

					{/* Internship Salary range */}
					{positionDetail?.workingMode &&
						(positionDetail?.workingMode === WorkingMode.INTERNSHIP ||
							positionDetail?.workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME) ? (
						<div>
							<div className="mb-2 block">
								<Label htmlFor="title" value="Internship Salary range (per month INR)" className={`${labelStyle}`} />
								<span className="text-red-600 pl-1">*</span>
							</div>

							<div className="flex items-center gap-2">
								<TextInput
									min={0}
									max={100}
									step="any"
									onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
									type="number"
									placeholder="0"
									name="internshipDuration"
									value={positionDetail?.internshipSalary?.min || 0}
									onChange={e => {
										const selectedValue = e.target.value || 0;
										if (+selectedValue <= 100) {
											setPositionDetail({
												...positionDetail,
												internshipSalary: {
													min: +selectedValue,
													max:
														+selectedValue > +(positionDetail?.internshipSalary?.max ?? 0)
															? +selectedValue
															: +(positionDetail?.internshipSalary?.max ?? 0),
												},
											});
										}
									}}
									className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow max-w-[50px]"
								/>
								<div className="h-[1px] w-[14px] bg-[#C7C9D9]"></div>
								<TextInput
									min={0}
									max={100}
									step="any"
									onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
									type="number"
									placeholder="2"
									name="internshipDuration"
									value={positionDetail?.internshipSalary?.max || 0}
									onChange={e => {
										const selectedValue = e.target.value || 0;
										if (+selectedValue <= 100 && +selectedValue >= +(positionDetail?.internshipSalary?.min || 0)) {
											setPositionDetail({
												...positionDetail,
												internshipSalary: { max: +selectedValue, min: +(positionDetail?.internshipSalary?.min ?? 0) },
											});
										}
									}}
									className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow max-w-[50px]"
								/>
								K
							</div>
						</div>
					) : null}

					{/* Salary range */}
					{positionDetail?.workingMode && positionDetail?.workingMode !== WorkingMode.INTERNSHIP && (
						<div>
							<div className="mb-2 block">
								<Label htmlFor="title" value="Salary range (lakhs per annum)" className={`${labelStyle}`} />
								<span className="text-red-600 pl-1">*</span>
							</div>

							<div className="flex items-center gap-2">
								<TextInput
									min={0}
									max={80}
									step="any"
									onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
									type="number"
									placeholder="0"
									name="internshipDuration"
									value={positionDetail?.salaryRange?.min || 0}
									onChange={e => {
										const selectedValue = e.target.value || 0;
										if (+selectedValue <= 80) {
											setPositionDetail({
												...positionDetail,
												salaryRange: {
													min: +selectedValue,
													max:
														+selectedValue > +positionDetail?.salaryRange?.max
															? +selectedValue
															: +positionDetail?.salaryRange?.max,
												},
											});
										}
									}}
									className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow max-w-[50px]"
								/>
								<div className="h-[1px] w-[14px] bg-[#C7C9D9]"></div>
								<TextInput
									min={0}
									max={80}
									step="any"
									onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
									type="number"
									placeholder="2"
									name="internshipDuration"
									value={positionDetail?.salaryRange?.max || 0}
									onChange={e => {
										const selectedValue = e.target.value || 0;
										if (+selectedValue <= 80 && +selectedValue >= +(positionDetail?.salaryRange?.min || 0)) {
											setPositionDetail({
												...positionDetail,
												salaryRange: { max: +selectedValue, min: positionDetail?.salaryRange?.min },
											});
										}
									}}
									className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow max-w-[50px]"
								/>
								LPA
							</div>
						</div>
					)}

					{/* Location */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="title" value="Location & work type" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<div className="flex gap-4">
							<div className="flex-1">
								<Select
									menuPlacement={'auto'}
									classNamePrefix="my-select-plans"
									className="single-value-select-tag"
									closeMenuOnSelect={true}
									placeholder="Mumbai"
									isMulti={true}

									options={locationPreferenceOptions.map(location => ({ value: location, label: location }))}
									value={positionDetail?.workLocations?.map(location => ({ value: location, label: location }))}
									onChange={(val: MultiValue<Dropdown>) => {

										setPositionDetail({
											...positionDetail,
											workLocations: val.map(item => item.value),
										});
									}}

								/>
							</div>
							<div className="flex-1">
								<Select
									menuPlacement={'auto'}
									options={positionTypes}
									value={
										positionDetail?.positionType
											? { label: positionDetail?.positionType, value: positionDetail?.positionType }
											: null
									}
									classNamePrefix="my-select-plans"
									className="single-value-select-tag"
									placeholder="Office"
									onChange={(val: SingleValue<Dropdown>) => {
										if (val?.value) {
											setPositionDetail({ ...positionDetail, positionType: val.value as PositionType });
										}
									}}
								/>
							</div>
						</div>
						{validatePositionWorkLocationAndWorkType() ? (
							<span className="text-red-600 text-sm">Please select work location and work type</span>
						) : null}
					</div>

					{/* Number of openings */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="vacancy" value="Number of openings" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>

						<div className="flex items-center gap-[8px]">
							<Button
								className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
								color="light"
								disabled={positionDetail.vacancy !== undefined && positionDetail.vacancy <= 1}
								onClick={() => {
									positionDetail.vacancy !== undefined &&
										setPositionDetail({ ...positionDetail, vacancy: --positionDetail.vacancy });
								}}
							>
								<IoMdRemove className="w-[24px] h-[24px]" />
							</Button>

							<TextInput
								min={1}
								max={50}
								onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
								type="number"
								placeholder="2"
								name="vacancy"
								value={positionDetail.vacancy}
								onChange={event => {
									if (+event?.target?.value <= 50)
										setPositionDetail({ ...positionDetail, vacancy: +event?.target?.value });
								}}
								className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow"
							/>

							<Button
								className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
								color="light"
								disabled={positionDetail.vacancy !== undefined && positionDetail.vacancy >= 50}
								onClick={() => {
									positionDetail.vacancy !== undefined &&
										setPositionDetail({ ...positionDetail, vacancy: ++positionDetail.vacancy });
								}}
							>
								<IoMdAdd className="w-[24px] h-[24px]" />
							</Button>
						</div>
					</div>

					{/* Company Pre-Placement Call Recording */}
					{(selectedCompany?.company?.prePlacementCallRecording || user?.registeredCompany?.prePlacementCallRecording) && (
						<div>
							<div className="mb-2 block">
								<Label htmlFor="prePlacementCallRecording" value="Company Pre-Placement Call Recording" className={`${labelStyle}`} />
							</div>
							<div className="space-y-2">
								<div className="p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
									<a
										href={selectedCompany?.company?.prePlacementCallRecording || user?.registeredCompany?.prePlacementCallRecording || ''}
										target="_blank"
										rel="noopener noreferrer"
										className="text-blue-600 dark:text-blue-400 hover:underline break-all"
									>
										{selectedCompany?.company?.prePlacementCallRecording || user?.registeredCompany?.prePlacementCallRecording}
									</a>
								</div>
								<div className="flex items-center gap-2">
									<Label htmlFor="showPrePlacementCallRecordingToStudent" value="Show to Students" className={`${labelStyle}`} />
									<ToggleSwitch
										id="showPrePlacementCallRecordingToStudent"
										checked={showPrePlacementCallRecordingToStudent}
										onChange={setShowPrePlacementCallRecordingToStudent}
										theme={{
											toggle: {
												checked: {
													color: {
														blue: 'bg-[#7777E7] border-[#7777E7]',
													},
												},
											},
										}}
									/>
								</div>
							</div>
						</div>
					)}

					{/* Prep Material Links */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="prepMaterial" value="Prep Material Links" className={`${labelStyle}`} />
						</div>
						<div className="space-y-2">
							{prepMaterialLinks.map((link, index) => (
								<div key={index} className="flex gap-2 items-center">
									<TextInput
										type="url"
										value={link}
										onChange={e => {
											const newLinks = [...prepMaterialLinks];
											newLinks[index] = e.target.value;
											setPrepMaterialLinks(newLinks);
										}}
										placeholder="https://..."
										className="flex-1"
										style={textInputStyle}
									/>
									{prepMaterialLinks.length > 1 && (
										<Button
											color="light"
											size="sm"
											onClick={() => {
												const newLinks = prepMaterialLinks.filter((_, i) => i !== index);
												setPrepMaterialLinks(newLinks);
											}}
											className="text-red-600 hover:text-red-700"
										>
											<IoMdRemove className="w-[20px] h-[20px]" />
										</Button>
									)}
								</div>
							))}
							<Button
								color="light"
								size="sm"
								onClick={() => setPrepMaterialLinks([...prepMaterialLinks, ''])}
								className="mt-2"
							>
								<IoMdAdd className="w-[20px] h-[20px] mr-1" />
								Add Link
							</Button>
						</div>
					</div>

					{/* Show Prep Material to Student Toggle */}
					<div className="flex items-center gap-2">
						<Label htmlFor="showPrepMaterialToStudent" value="Show Prep Material to Students" className={`${labelStyle}`} />
						<ToggleSwitch
							id="showPrepMaterialToStudent"
							checked={showPrepMaterialToStudent}
							onChange={setShowPrepMaterialToStudent}
							theme={{
								toggle: {
									checked: {
										color: {
											blue: 'bg-[#7777E7] border-[#7777E7]',
										},
									},
								},
							}}
						/>
					</div>
				</>
			) : (
				<>
					{/* Select HR Email */}
					<div>
						<div className="mb-2">
							<div className="block">
								<Label htmlFor="title" value="HR Email" className={`${labelStyle}`} />
								<span className="text-red-600 pl-1">*</span>
							</div>

							{positionDetail?.newRecruiterDetails?.email &&
								!validateEmailAddress(positionDetail?.newRecruiterDetails?.email) ? (
								<span className="text-red-600 text-sm">Please enter a valid email address</span>
							) : null}
						</div>

						<HRSelection
							value={positionDetail?.newRecruiterDetails?.email}
							recruitersData={recruitersData}
							textInputStyle={textInputStyle}
							companyId={companyId || ''}
							onChange={handleRecruiterEmailChange}
						/>
					</div>

					{/* HR Name */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="techStack" value="HR Name" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>

							{/* <div>
								{positionDetail?.newRecruiterDetails?.name ? (
									<span className="text-red-600 text-sm">Please enter a valid name</span>
								) : null}
							</div> */}
						</div>
						<TextInput
							placeholder="Suman"
							className="[&_input]:border [&_input]:!border-[#D1D5DB] [&_input]:bg-white  dark:[&_input]:bg-[#374151]"
							type="text"
							color="purplePlaced"
							disabled={disableNewRecruiterFields && existingRec?.name ? true : false}
							value={positionDetail?.newRecruiterDetails?.name}
							onChange={e => handleRecruiterDetailsChange({ name: e.target.value })}
						/>
					</div>

					{/* HR Phone */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="techStack" value="HR Phone" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>

							<div>
								{positionDetail.newRecruiterDetails?.phone &&
									(!validatePhoneNumber(positionDetail.newRecruiterDetails?.phone) ||
										!(positionDetail.newRecruiterDetails?.phone?.length === 10)) ? (
									<span className="text-red-600 text-sm">Please enter a valid phone number</span>
								) : null}
							</div>
						</div>
						<TextInput
							disabled={disableNewRecruiterFields && existingRec?.phone ? true : false}
							placeholder="9876xxxxxx"
							className="[&_input]:border [&_input]:!border-[#D1D5DB] [&_input]:bg-white  dark:[&_input]:bg-[#374151]"
							type="number"
							color="purplePlaced"
							value={positionDetail.newRecruiterDetails?.phone}
							onChange={e => handleRecruiterDetailsChange({ phone: e.target.value })}
						/>
					</div>

					<div>
						<div className="mb-2 block">
							<Label htmlFor="techStack" value="Designation" className={`${labelStyle}`} />
						</div>
						<TextInput
							placeholder="HR, CEO"
							className="[&_input]:border [&_input]:!border-[#D1D5DB] [&_input]:bg-white  dark:[&_input]:bg-[#374151]"
							color="purplePlaced"
							value={positionDetail.newRecruiterDetails?.recruiterDesignation}
							onChange={e => handleRecruiterDetailsChange({ recruiterDesignation: e.target.value || '' })}
						/>
					</div>

					{/* Hide Recommendations for HR */}
					{isAccountManagerLoggedIn ? (
						<div>
							<Label htmlFor="hideRecommendations" value="Hide Recommendations" className={`${labelStyle}`} />

							<div className="min-h-[38px] flex items-center">
								<ToggleSwitch
									id="hideRecommendations"
									theme={{
										toggle: {
											checked: {
												color: {
													blue: 'bg-[#7777E7] border-[#7777E7]',
												},
											},
										},
									}}
									checked={positionDetail?.newRecruiterDetails?.hideRecommendations || false}
									onChange={() => {
										handleRecruiterDetailsChange({
											hideRecommendations: !positionDetail?.newRecruiterDetails?.hideRecommendations,
										});
									}}
								/>
							</div>
						</div>
					) : null}

					<div className="flex-grow"></div>
				</>
			)}

			{user?.phone ? null : (
				<div>
					<div className="mb-2 block">
						<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="phone" value="Phone number " />
						<span className="text-red-600">*</span>
					</div>
					<TextInput
						id="phone"
						type="tel"
						pattern={`^[0-9]{10}$`}
						placeholder="9876543210"
						required={true}
						className="[&_input:focus]:ring-transparent [&_input]:!bg-[#F9FAFB] [&_input:focus]:border-[#1C64F2] RemoveInputArrow"
						value={positionDetail.phone}
						onChange={e =>
							setPositionDetail({
								...positionDetail,
								phone: e.target.value,
							})
						}
						style={textInputStyle}
					/>

					{validateUserPhone() ? <span className="text-red-600 text-sm">Please enter a valid phone number</span> : null}
				</div>
			)}

			<ConfirmCompanyNameModal
				selectedCompany={selectedCompanyId}
				setSelectedCompany={setSelectedCompanyId}
				isOpen={isOpen}
				onClose={onClose}
				handleSubmit={handleSubmit}
			/>

			<Drawer
				isModal={!isMobile}
				isOpen={openAccountManagerDrawer}
				onClose={() => {
					setOpenAccountManagerDrawer(false);
				}}
				minHeight="fit-content"
				header={<></>}
				body={<CompanyAccountManagers accountManagers={accountManagers} />}
			/>

			<div className="bg-white dark:bg-[#111E2C] sticky flex items-center gap-5 bottom-0 mb-[-16px] md:mb-[-24px] mx-[-16px] md:mx-[-24px] shadow-up px-[16px] md:px-[24px] rounded-b-[12px]">
				<Button
					type="submit"
					color="purplePlaced"
					onClick={() => setHasTouchedContinue(true)}
					className="my-3 w-full"
					disabled={hasTouchedContinue ? isDisabledCTA : false}
				>
					{registerFn.isLoading ? <Spinner /> : 'Continue'}
				</Button>

				{isAccountManagerLoggedIn ? null : (
					<div className="lg:hidden">
						<button
							type="button"
							onClick={() => setOpenAccountManagerDrawer(true)}
							className="h-[40px] w-[40px] text-[#7777E7] transition-all hover:bg-[#6666ed] hover:text-white border border-[#7777E7] rounded-[8px] flex items-center justify-center bg-transparent"
						>
							<svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									d="M7.99987 11.1226C5.92787 11.0864 4.40276 8.51927 4.40453 6.63439C4.40453 6.29194 4.40453 5.9495 4.40453 5.60727C4.39365 3.70661 6.0132 2.06305 7.99987 2.01172C9.98653 2.06305 11.6059 3.70661 11.5952 5.60705C11.5952 5.9495 11.5952 6.29194 11.5952 6.63416C11.5968 8.51927 10.0719 11.0862 7.99987 11.1226Z"
									fill="currentColor"
								/>
								<path
									d="M10.2621 12.3125L7.99988 15.2938L5.73766 12.3125C2.84922 12.4658 0.552551 14.8496 0.552551 17.7763V18.7607C0.552551 19.4463 1.10833 20.0018 1.79388 20.0018H14.2061C14.8917 20.0018 15.4474 19.4461 15.4474 18.7607V17.7763C15.4472 14.8496 13.1506 12.4658 10.2621 12.3125Z"
									fill="currentColor"
								/>
								<path
									d="M13.6186 4.734H13.5057C13.0975 2.05844 10.7877 0 7.99991 0C5.21213 0 2.90235 2.05844 2.49413 4.734H2.42346C1.9268 4.734 1.52435 5.13667 1.52435 5.63311V6.35556C1.52435 6.85222 1.92702 7.25467 2.42346 7.25467H2.77324C3.12346 7.25467 3.40724 6.97089 3.40724 6.62067V5.73844C3.40724 3.29489 5.24702 1.15756 7.68524 0.994C10.3592 0.814667 12.5926 2.93956 12.5926 5.576L12.5837 7.584C12.5837 8.312 12.081 9.42044 11.6237 9.91711C11.545 9.88267 11.4615 9.868 11.3728 9.868C10.9646 9.868 10.635 10.1976 10.635 10.6058V10.6107C10.635 10.886 10.7924 11.132 11.0237 11.25C11.127 11.3091 11.245 11.3436 11.3728 11.3436C11.781 11.3436 12.1106 11.0091 12.1106 10.6058C12.1106 10.5664 12.1057 10.5271 12.1008 10.4878C12.7157 9.85333 13.3215 8.54311 13.3215 7.584V7.25444H13.5761H13.6184C14.115 7.25444 14.5175 6.85178 14.5175 6.35533V5.63333C14.5177 5.13667 14.115 4.734 13.6186 4.734Z"
									fill="currentColor"
								/>
							</svg>
						</button>
					</div>
				)}
			</div>
		</form>
	);
}

export default CreatePosition;

